import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlanMap } from "@/components/PlanMap";
import { PlanForm } from "@/components/planner/PlanForm";
import { Itinerary } from "@/components/planner/Itinerary";
import { GeneratingOverlay } from "@/components/GeneratingOverlay";

export default function Planner() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [activeSid, setActiveSid] = useState(null);
  const [previewStart, setPreviewStart] = useState(user?.profile?.home || null);

  useEffect(() => {
    setEditing(false);
    if (!id) return setPlan(null);
    if (plan?.id === id) return;
    api.get(`/plans/${id}`).then((r) => setPlan(r.data)).catch((e) => { toast.error(errMsg(e)); nav("/"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generate = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post("/plans/generate", payload);
      setPlan(data);
      setEditing(false);
      nav(`/plans/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const showForm = !id || editing;
  const mapStart = showForm ? previewStart : plan?.start;
  return (
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-4rem)]" data-testid="planner-page">
      <aside className="order-2 lg:order-1 w-full lg:w-[460px] xl:w-[500px] shrink-0 lg:overflow-y-auto border-r border-stone-200 bg-[#F3F6EF]">
        {showForm ? (
          <PlanForm key={editing ? "edit" : "new"} onGenerate={generate} onStartChange={setPreviewStart} initial={editing ? plan : null} />
        ) : plan ? (
          <Itinerary plan={plan} setPlan={setPlan} onEdit={() => { setPreviewStart(plan.start); setEditing(true); }} activeSid={activeSid} setActiveSid={setActiveSid} />
        ) : (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        )}
      </aside>
      <section className="order-1 lg:order-2 relative flex-1 h-[45vh] lg:h-auto z-0">
        <PlanMap start={mapStart} stops={showForm ? [] : plan?.stops || []} activeSid={activeSid} onSelect={setActiveSid} />
        {showForm && (
          <div className="absolute bottom-5 left-5 right-5 lg:right-auto lg:max-w-sm z-[500] bg-white/85 backdrop-blur-md rounded-2xl border border-stone-200 p-4 shadow-lg pointer-events-none">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#23603F]">Any US city</p>
            <p className="mt-1 text-sm text-stone-700">Choose a start, set your time, and we'll map an outing of real places. Nothing is shared unless you choose to share it.</p>
          </div>
        )}
      </section>
      {busy && <GeneratingOverlay />}
    </div>
  );
}
